// Any query generated by `toQuery` can be revert back to object using `fromQuery` function
// Note: Query returned is URL safe.
export type Query = Partial<{
  limit: number | string;
  offset: number | string;
  search: string;
  sort: { field: string; order: string };
  filter: { [key: string]: string[] | number[] };
}>;

/**
 * @deprecated We should not be manually creating queryStrings, and should instead generate
 * a flattened object and pass it to `new URLSearchParams(obj)` which has a reliable
 * .toString() method to generate the query string.
 */
export const toQuery = (payload: Query) => {
  const { limit, offset, search, filter, sort, ...preserved } = payload;

  const hasSearch = (search?.length ?? 0) > 0;
  const hasFilter = filter && Object.keys(filter).length > 0;
  const hasSort = sort?.order && sort?.field;
  const hasLimit = !!limit;
  const hasOffset = offset && Number(offset) > 1;
  const hasPreserved = Object.entries(preserved).length > 0;

  let q = '?';

  if (hasLimit) {
    q += `limit=${limit}&`;
  }

  if (hasOffset) {
    q += `offset=${offset}&`;
  }

  if (hasSearch) {
    q += `search=${encodeURIComponent(search)}&`;
  }

  if (hasSort) {
    // encode each field but keep the `,` delimiter of multi-field sorts literal
    const field = sort.field
      .split(',')
      .map(item => encodeURIComponent(item))
      .join(',');

    if (sort.order === 'ascend') {
      q += `sort=${field}&`;
    }

    if (sort.order === 'descend') {
      q += `sort=-${field}&`;
    }
  }

  if (hasFilter) {
    // iterate through the `filter` object, append all key and value sets to the query.
    // for example:
    // `{ foo: [1, 2, 3], bar: [3]}` => `foo=1,2,3&bar=3&`
    q += Object.entries(filter).reduce(
      (prev, [key, value]) =>
        `${prev === '' ? prev : `${prev}&`}${encodeURIComponent(key)}=${(
          value as Array<string | number>
        )
          .map(item => encodeURIComponent(item))
          .join(',')}`,
      '',
    );

    if (hasPreserved) {
      // iterate through the `preserved` object, append all key and value sets to the query.
      // for example:
      // `{ foo: 'a', bar: 'b'}` => `foo=a&bar=b`
      q += `&${Object.entries(preserved)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&')}`;
    }
  }

  // check whether or not the last character in the newly formed query is either `&` or `?`,
  // then remove it as query may not have any params that we are setting and in that case it
  // would be just a `?` which is an edge case, similarly since all params are being checked
  // and added to the query sequentially, so it may have a leftover `&` character.
  if (q[q.length - 1] === '&' || q[q.length - 1] === '?') {
    q = q.slice(0, -1);
  }

  return q;
};

// fromQuery takes a query string and then generates an object based on the provided string.
//
// Expected params:
// - `limit`
// - `offset`
// - `search`
// - `sort`
// Any other param besides aforementioned params would be considered a filter and it will be
// put in the `filter` object
//
// Any object generated by `fromQuery` can be revert back to string using `toQuery` function
type ReducerPayload = Omit<Query, 'sort'> & { sort?: string };

/**
 * @deprecated We should not be manually parsing query strings, and should instead pass it
 * to `new URLSearchParams(queryString)` and then either use that object, or reduce the entries
 * back into an object, after which we can restructure it from a flat object as needed.
 */
export const fromQuery = (query: string): Query => {
  if (query.length === 0) {
    return {};
  }

  // drop empty-value entries (e.g. `?foo`) so they don't surface as bogus filters
  const params = Array.from(new URLSearchParams(query).entries()).filter(
    ([, value]) => value !== '',
  );

  // check if the provided key is a field API understands
  const isDefined = (key: string) => ['search', 'sort', 'limit', 'offset'].includes(key);

  // check if the provided key should be preserved in query
  const shouldPreserve = (key: string) => ['tab'].includes(key);

  // create an object from all the parts which have their keys defined
  const { sort, ...payload } = params
    // filter through all keys, only look for defined keys
    .filter(([key]) => isDefined(key) || shouldPreserve(key))
    // reduce the remaining keys and values to form an object
    .reduce<ReducerPayload>((object, [key, value]) => ({ [key]: value, ...object }), {});

  // check if `sort` key is defined in the query – in the case of it being defined, further
  // validate if sort is defined in a particular format which API follow:
  // `sort=field`   -> sort the records using `field` in ascending order
  // `sort=-field`  -> sort the records using `field` in descending order
  // replace the string for `sort` key to an explicit format
  // for example:
  // `sort=-foo` => { order:  'descend', field: 'foo'}
  let sortPayload: Pick<Query, 'sort'> = {};
  if (sort) {
    // check if the string for sort starts with `-` character
    if (sort[0] === '-') {
      sortPayload = { sort: { order: 'descend', field: sort.substr(1, sort.length) } };
    } else {
      sortPayload = { sort: { order: 'ascend', field: sort } };
    }
  }

  // create filter object using the remaining keys from the query parts
  const filter = params
    // filter through all the keys, look for only undefined keys
    .filter(([key]) => !isDefined(key) && !shouldPreserve(key))
    // reduce the key/value pairs into an object
    .reduce<{ [key: string]: string[] }>(
      (object, [key, value]) => ({ [key]: value.split(','), ...object }),
      {},
    );

  // check if there are no filters defined and for such cases, return the `payload`
  // without `filter` object
  if (Object.keys(filter).length === 0) {
    return { ...payload, ...sortPayload };
  }

  return { ...payload, ...sortPayload, filter };
};

// mergeQueries combines all the provided N queries
// it will remove duplicate keys and if they have different values
// they will be overridden by the latest value
export const mergeQueries = (...queries: string[]) => {
  const source = queries
    // create new `URLSearchParams` from raw string queries
    .map(query => new URLSearchParams(query))
    // reduce all `URLSearchParams` to entries with keys and values
    .reduce<[string, string][]>((prev, next) => [...prev, ...Array.from(next.entries())], [])
    // create an object from key/value pairs
    .reduce((prev, [key, value]) => ({ ...prev, [key]: value }), {});

  const params = new URLSearchParams(source);

  params.sort();

  return params;
};

/**
 * compareQuery takes two queries and returns if they're the same
 * regardless of their alphabetical order.
 */
export const compareQuery = (left: string, right: string) => {
  const leftParams = new URLSearchParams(left);
  const rightParams = new URLSearchParams(right);

  leftParams.sort();
  rightParams.sort();

  return leftParams.toString() === rightParams.toString();
};
